import { LitElement, PropertyValues, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { AtlFooterProps } from "./footer.props.ts";

import "../placeholders/index.ts";

import * as TagNames from "./footer.tagnames.ts";
import { getGlobalStyleSheets } from "../../global-styles.ts";
import classNames from "classnames";
import footerStyles from "./footer.scss?inline";

@customElement(TagNames.Footer)
export class AtlFooter extends LitElement implements AtlFooterProps {
  declare label: string;
  declare showPlaceholder: boolean;
  declare fillWidth: boolean;
  private _shouldCenter: boolean;

  static properties = {
    label: { type: String },
    showPlaceholder: { type: Boolean },
    fillWidth: { type: Boolean, attribute: "fill-width", reflect: true },
  };

  static styles = [...getGlobalStyleSheets(), footerStyles];

  constructor() {
    super();
    this.fillWidth = false;
    this._shouldCenter = window.innerWidth > 1920;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("resize", this._handleResize);
  }

  disconnectedCallback(): void {
    window.removeEventListener("resize", this._handleResize);
    super.disconnectedCallback();
  }

  private _renderLinkPlaceholder = () => {
    if (!this.showPlaceholder) return nothing;

    const placeholder = html`
      <atlantis-footer-link show-placeholder></atlantis-footer-link>
    `;
    return html`${placeholder}${placeholder}${placeholder}`;
  };

  private _renderLabels = () => {
    if (this.showPlaceholder) {
      return html`
        <span>
          <atlantis-paragraph-placeholder
            sentences="2"
          ></atlantis-paragraph-placeholder>
        </span>
      `;
    }
    return html`<span>${this.label}</span>`;
  };

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    if (changedProperties.has("showPlaceholder")) {
      this._onSlotChange();
    }
  }

  private _onSlotChange = () => {
    const slot = this.shadowRoot?.querySelector("slot");
    const nodes = slot?.assignedNodes({ flatten: true }) || [];

    nodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE)
        if (
          node instanceof HTMLElement &&
          node.tagName.toLowerCase() === TagNames.FooterLink.toLowerCase()
        ) {
          if (this.showPlaceholder) {
            node.setAttribute("show-hidden", "");
          } else {
            node.removeAttribute("show-hidden");
          }
        }
    });
  };

  render() {
    return html`
      <footer
        class=${classNames(
          "footer",
          !this.fillWidth && this._shouldCenter && "footer-centered"
        )}
        role="contentinfo"
      >
        <div class="container-fluid footer-container">
          <div class="footer-links">
            ${this._renderLinkPlaceholder()}
            <slot @slotchange=${this._onSlotChange}></slot>
          </div>
          <div>${this._renderLabels()}</div>
        </div>
      </footer>
    `;
  }

  private _handleResize = () => {
    this._shouldCenter = window.innerWidth > 1920;
    this.requestUpdate();
  };
}
